'use client';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Shield, Users, AlertTriangle, Scale, Globe, Mail, Clock } from 'lucide-react';
import Footer from '@/app/public/component/footer';

export default function TermsOfServicePage() {
  return (
    <div className="h-dvh bg-linear-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 w-dvw overflow-y-scroll">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileText className="h-8 w-8 text-blue-600" />
            <h1 className="text-4xl font-bold bg-linear-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Terms of Service
            </h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            The rules and guidelines for using <span className="font-semibold text-blue-600">LitePop</span> by <span className="font-semibold text-purple-600">DeepZone</span>
          </p>
          <Badge variant="outline" className="mt-4">
            <Clock className="h-3 w-3 mr-1" />
            Last updated: {new Date().toLocaleDateString()}
          </Badge>
        </div>

        {/* Quick Summary */}
        <Card className="mb-8 border-blue-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Quick Summary
            </CardTitle>
            <CardDescription>
              Key points you should know about using our service
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm">• By using LitePop, you agree to these terms</p>
                <p className="text-sm">• You're responsible for content you post</p>
                <p className="text-sm">• We monitor and may remove violating content</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm">• Service availability isn't guaranteed 24/7</p>
                <p className="text-sm">• Contact us at privacy@deepzone.com for questions</p>
                <p className="text-sm">• Terms may be updated with notice</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table of Contents */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Table of Contents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-2 text-sm">
              <a href="#agreement" className="text-blue-600 hover:underline">1. Agreement to Terms</a>
              <a href="#intellectual-property" className="text-blue-600 hover:underline">2. Intellectual Property Rights</a>
              <a href="#user-representations" className="text-blue-600 hover:underline">3. User Representations</a>
              <a href="#prohibited-activities" className="text-blue-600 hover:underline">4. Prohibited Activities</a>
              <a href="#user-contributions" className="text-blue-600 hover:underline">5. User Generated Contributions</a>
              <a href="#contribution-license" className="text-blue-600 hover:underline">6. Contribution License</a>
              <a href="#submissions" className="text-blue-600 hover:underline">7. Submissions</a>
              <a href="#site-management" className="text-blue-600 hover:underline">8. Site Management</a>
              <a href="#fees" className="text-blue-600 hover:underline">9. Fees and Charges</a>
              <a href="#termination" className="text-blue-600 hover:underline">10. Term and Termination</a>
              <a href="#modifications" className="text-blue-600 hover:underline">11. Modifications and Interruptions</a>
              <a href="#governing-law" className="text-blue-600 hover:underline">12. Governing Law</a>
              <a href="#dispute-resolution" className="text-blue-600 hover:underline">13. Dispute Resolution</a>
              <a href="#corrections" className="text-blue-600 hover:underline">14. Corrections</a>
              <a href="#disclaimer" className="text-blue-600 hover:underline">15. Disclaimer</a>
            </div>
          </CardContent>
        </Card>

        {/* Agreement to Terms */}
        <Card id="agreement" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              1. Agreement to Terms
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              DeepZone's Terms of Service – These Terms and Conditions constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and DeepZone ("Company," "we," "us," or "our"), concerning your access to and use of the LitePop website as well as any other media form, media channel, mobile website or mobile application related, linked, or otherwise connected thereto (collectively, the "Site"). You agree that by accessing the Site, you have read, understood, and agreed to be bound by all of these Terms and Conditions.
            </p>
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                IF YOU DO NOT AGREE WITH ALL OF THESE TERMS AND CONDITIONS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SITE AND YOU MUST DISCONTINUE USE IMMEDIATELY.
              </p>
            </div>
            <p className="text-sm leading-relaxed">
              Supplemental terms and conditions or documents that may be posted on the Site from time to time are hereby expressly incorporated herein by reference. We reserve the right, in our sole discretion, to make changes or modifications to these Terms and Conditions at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of these Terms and Conditions, and you waive any right to receive specific notice of each such change.
            </p>
          </CardContent>
        </Card>

        {/* Intellectual Property Rights */}
        <Card id="intellectual-property" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-500" />
              2. Intellectual Property Rights
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              Unless otherwise indicated, the Site is our proprietary property and all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics on the Site (collectively, the "Content") and the trademarks, service marks, and logos contained therein (the "Marks") are owned or controlled by us or licensed to us, and are protected by copyright and trademark laws and various other intellectual property rights and unfair competition laws of Australia, international copyright laws, and international conventions.
            </p>
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                The Content and the Marks are provided on the Site "AS IS" for your information and personal use only.
              </p>
            </div>
            <p className="text-sm leading-relaxed">
              Provided that you are eligible to use the Site, you are granted a limited license to access and use the Site and to download or print a copy of any portion of the Content to which you have properly gained access solely for your personal, non-commercial use. We reserve all rights not expressly granted to you in and to the Site, the Content and the Marks.
            </p>
          </CardContent>
        </Card>

        {/* User Representations */}
        <Card id="user-representations" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-green-500" />
              3. User Representations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              By using the Site, you represent and warrant that:
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">•</span>
                <span>You have the legal capacity and you agree to comply with these Terms and Conditions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">•</span>
                <span>You are not a minor in the jurisdiction in which you reside</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">•</span>
                <span>You will not access the Site through automated or non-human means, whether through a bot, script, or otherwise</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">•</span>
                <span>You will not use the Site for any illegal or unauthorized purpose</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">•</span>
                <span>Your use of the Site will not violate any applicable law or regulation</span>
              </li>
            </ul>
            <p className="text-sm leading-relaxed">
              If you provide any information that is untrue, inaccurate, not current, or incomplete, we have the right to suspend or terminate your account and refuse any and all current or future use of the Site (or any portion thereof).
            </p>
          </CardContent>
        </Card>

        {/* Prohibited Activities */}
        <Card id="prohibited-activities" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              4. Prohibited Activities
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              You may not access or use the Site for any purpose other than that for which we make the Site available. The Site may not be used in connection with any commercial endeavours except those that are specifically endorsed or approved by us.
            </p>
            <p className="text-sm font-medium">As a user of the Site, you agree not to:</p>
            <ScrollArea className="h-64 w-full border rounded-lg p-4">
              <div className="space-y-2 text-sm">
                <p>• Systematically retrieve data or other content from the Site to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us</p>
                <p>• Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords</p>
                <p>• Circumvent, disable, or otherwise interfere with security-related features of the Site</p>
                <p>• Disparage, tarnish, or otherwise harm, in our opinion, us and/or the Site</p>
                <p>• Use any information obtained from the Site in order to harass, abuse, or harm another person</p>
                <p>• Make improper use of our support services or submit false reports of abuse or misconduct</p>
                <p>• Use the Site in a manner inconsistent with any applicable laws or regulations</p>
                <p>• Engage in unauthorized framing of or linking to the Site</p>
                <p>• Upload or transmit viruses, Trojan horses, or other material that interferes with any party's uninterrupted use and enjoyment of the Site</p>
                <p>• Engage in any automated use of the system, such as using scripts to send comments or messages</p>
                <p>• Delete the copyright or other proprietary rights notice from any Content</p>
                <p>• Attempt to impersonate another user or person or use the username of another user</p>
                <p>• Interfere with, disrupt, or create an undue burden on the Site or the networks or services connected to the Site</p>
                <p>• Harass, annoy, intimidate, or threaten any of our employees or agents engaged in providing any portion of the Site to you</p>
                <p>• Copy or adapt the Site's software, including but not limited to Flash, PHP, HTML, JavaScript, or other code</p>
                <p>• Use the Site as part of any effort to compete with us or otherwise use the Site and/or the Content for any revenue-generating endeavour or commercial enterprise</p>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* User Generated Contributions */}
        <Card id="user-contributions" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              5. User Generated Contributions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              We may provide you with the opportunity to create, submit, post, display, transmit, perform, publish, distribute, or broadcast content and materials to us or on the Site, including but not limited to text, writings, video, audio, photographs, graphics, comments, suggestions, or personal information or other material (collectively, "Contributions").
            </p>
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Contributions may be viewable by other users of the Site and through third-party websites. As such, any Contributions you transmit may be treated in accordance with the Site Privacy Policy.
              </p>
            </div>
            <p className="text-sm leading-relaxed">
              When you create or make available any Contributions, you thereby represent and warrant that your Contributions comply with our community guidelines and do not violate any third-party rights, applicable laws, or these Terms and Conditions.
            </p>
          </CardContent>
        </Card>

        {/* Fees and Charges */}
        <Card id="fees" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-green-500" />
              9. Fees and Charges
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              DeepZone requires you to subscribe to a Plan. Your Plan may be month-to-month or may have a minimum term such as 12 months. It will be billed, collected, must be paid and may be cancelled under the same terms of your applicable Plan.
            </p>
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Applicable fees and charges for DeepZone's Services are subject to change. If they are changed, reasonable notice will be provided to you and if you are inside a minimum contract term you will be provided with a reasonable time period to cancel your services without penalty. Typically this will be at least 14 days.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Governing Law */}
        <Card id="governing-law" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-purple-500" />
              12. Governing Law
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm leading-relaxed">
              These Terms shall be governed by and defined following the laws of Australia. DeepZone and yourself irrevocably consent that the courts of Australia shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these terms.
            </p>
          </CardContent>
        </Card>

        {/* Dispute Resolution */}
        <Card id="dispute-resolution" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-blue-500" />
              13. Dispute Resolution
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-semibold text-sm">Binding Arbitration</h4>
            <p className="text-sm leading-relaxed">
              Any dispute arising out of or in connection with this contract, including any question regarding its existence, validity or termination, shall be referred to and finally resolved by the Australian Centre for International Commercial Arbitration (ACICA), which, as a result of referring to it, is considered as part of this clause.
            </p>
            <h4 className="font-semibold text-sm">Restrictions</h4>
            <p className="text-sm leading-relaxed">
              The Parties agree that any arbitration shall be limited to the Dispute between the Parties individually. To the full extent permitted by law, there is no right or authority for any Dispute to be arbitrated on a class-action basis or to utilize class action procedures.
            </p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="border-green-200 dark:border-green-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-green-500" />
              Contact Us
            </CardTitle>
            <CardDescription>
              Questions about these Terms of Service?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">
              In order to resolve a complaint regarding the Site or to receive further information regarding use of the Site, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Email: privacy@deepzone.com
              </p>
            </div>
          </CardContent>
        </Card>

        <Separator className="my-8" />
        
        <div className="text-center text-sm text-muted-foreground">
          <p>© 2024 DeepZone. All rights reserved.</p>
          <p className="mt-2">This Terms of Service page was last updated on {new Date().toLocaleDateString()}.</p>
        </div>
      </div>
      <Footer />
    </div>
  );
}